import '@fontsource-variable/inter';
import '@fontsource-variable/space-grotesk';
import '@fontsource-variable/jetbrains-mono';
import '@fontsource/barlow-condensed/600.css';
import '@fontsource/barlow-condensed/700.css';
import './globals.css';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { Desk } from '@/components/desk';
import { DeskPage } from '@/components/desk-page';
import { MenuApp } from '@/components/menu-app';
import { useMe } from '@/lib/client/use-me';
import { AdminApp } from '@/components/admin-app';
import { BoardApp } from '@/components/board-app';
import { JournalApp } from '@/components/journal-app';
import { MoversApp } from '@/components/movers-app';
import { PlansApp } from '@/components/plans-app';
import { TodayAlerts, TodayApp } from '@/components/today-app';
import { SettingsApp } from '@/components/settings-app';
import { ShortlistApp } from '@/components/shortlist-app';
import { StockApp } from '@/components/stock-app';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LoginPage } from '@/pages/login';
import { API_URL } from '@/lib/api';
import { registerServiceWorker } from '@/lib/pwa';

// Kite sends the browser to the redirect URL set in its developer console with ?request_token=…
// If that URL points at the app (any path here) rather than the API, pass the login on to the API's
// callback instead of dropping it.
{
  const q = new URLSearchParams(location.search);
  if (q.get('request_token') && q.get('action') === 'login') {
    location.replace(`${API_URL}/kite/callback${location.search}`);
  }
}

const TITLES: Record<string, string> = { '/': 'MoneyHQ — where the market’s money is moving today', '/movers': 'Movers · MoneyHQ', '/plans': 'Plans · MoneyHQ', '/today': 'Today · MoneyHQ', '/shortlist': 'Shortlist · MoneyHQ' };

/** The desk on every signed-in page, for approved members (it uses live data and paper trades). */
function DeskHost() {
  const { pathname } = useLocation();
  const me = useMe();
  // The desk page is the desk itself: no panel over it.
  return pathname.startsWith('/login') || pathname === '/desk' || !me?.access ? null : <Desk />;
}

function Titles() {
  const { pathname } = useLocation();
  useEffect(() => { if (TITLES[pathname]) document.title = TITLES[pathname]; }, [pathname]);
  return null;
}

registerServiceWorker();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <TooltipProvider delayDuration={200}>
        <Titles />
        <DeskHost />
        <TodayAlerts />
        <Routes>
          <Route path="/" element={<BoardApp />} />
          <Route path="/desk" element={<DeskPage />} />
          <Route path="/menu" element={<MenuApp />} />
          <Route path="/movers" element={<MoversApp />} />
          <Route path="/plans" element={<PlansApp />} />
          <Route path="/today" element={<TodayApp />} />
          <Route path="/shortlist" element={<ShortlistApp />} />
          <Route path="/stock/:symbol" element={<StockApp />} />
          <Route path="/admin" element={<AdminApp />} />
          <Route path="/settings" element={<SettingsApp />} />
          <Route path="/journal" element={<JournalApp />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </TooltipProvider>
      {/* Phones: toasts sit above the bottom tab bar. */}
      <Toaster position="bottom-center" mobileOffset={{ bottom: 'calc(5.5rem + env(safe-area-inset-bottom))' }} />
    </BrowserRouter>
  </StrictMode>,
);
